import { Activity, Radar, AlertTriangle } from "lucide-react";

export const EmailBlacklistsHowListed = () => {
  return (
    <section className="py-10 sm:py-12">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3 text-center">
        How a domain actually gets listed
      </h2>
      <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8">
        There are two doors onto a blacklist, and only one of them requires you to have sent
        anything at all.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Behavioral listings
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            The domain appeared in emails that reached spam traps. A spam trap is an address
            that no real person owns, so nobody could have opted in and nobody could have
            handed it over. Mail arriving at one is evidence that the address list was scraped
            or purchased rather than earned.
          </p>
          <ul className="space-y-2.5">
            {[
              "Triggered by real sending activity",
              "Strongly associated with poor list sourcing",
              "Often accompanied by high bounce and complaint rates",
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Radar className="w-5 h-5 text-primary" />
            Predictive listings
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Blacklists also ingest feeds of newly registered domains and preemptively list
            clusters that match known spam infrastructure patterns. No message needs to exist
            for this to happen. The signal is the registration pattern itself.
          </p>
          <ul className="space-y-2.5">
            {[
              "Many fresh domains sharing one brand root with common modifiers",
              "Registrations clustered closely together in time",
              "Very young domain age",
              "Certain top level domains that carry elevated abuse rates",
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-warning mt-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 bg-warning/5 border border-warning/20 rounded-2xl p-5 sm:p-6">
        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-warning" />
          Predictive listings can happen with zero emails sent
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          This is the part that surprises most senders. A domain registered last week, never
          used, can already be listed because it resembles infrastructure that was abused
          before. Pattern sweeping of this kind has affected large parts of the outbound
          industry recently, particularly teams that buy domain sets in bulk. Spamhaus itself
          has published research on how bulk registration patterns feed its blocklists, which
          is worth reading before assuming a listing implies misconduct.
        </p>
      </div>
    </section>
  );
};